/**
 * `ObservedActivityPanel` — the OBSERVED evidence tier for the Live
 * Agent view (ADR-0161 §5).
 *
 * Renders as a slim horizontal strip directly UNDER `PhaseTimeline`
 * and ABOVE the two-column `AgentColumn` / `EndpointColumn` grid.
 * The strip's mono `OBSERVED` kicker mirrors the `LIVE` / `ILLUSTRATION`
 * tags in `EndpointColumn.tsx` so all three tiers read the same way
 * across the view.
 *
 * States (matches `useAgentActivity` tagged shape):
 *   - `idle`     — hook was gated out (`edgeAgentIds` empty or no site).
 *                  This panel is only mounted when the site + protocol
 *                  gate passes at the parent, so idle here means the
 *                  hook is still resolving; render a small loading
 *                  placeholder rather than crash.
 *   - `loading`  — small skeleton row (no full spinner).
 *   - `error`    — quiet mono footer "(couldn't load activity)".
 *   - `ready`    — summary line + up to 5 rows visible, internal scroll
 *                  to the wire's LIMIT 20.
 *
 * Empty state (`visit_count === 0`): one honest line
 * `"No observed visits from {agent} yet."` — no table, no scroll.
 *
 * No animation; OBSERVED is not part of the step-through timeline.
 * The strip is rendered whenever data lands and stays static.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Radio } from 'lucide-react';

import type { ReportingAgentActivityVisit } from '@sill/types';

import { Card, Pill } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import type { UseAgentActivityState } from '../hooks/useAgentActivity.js';
import type { AgentDef } from '../roster.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/**
 * True below 640px — the recent-visits table's six fixed-width columns
 * are unreadable narrower than that (ADR-0163 §6), so the panel
 * re-lays them as stacked per-visit cards. 640 (not 768) keeps the
 * table on iPad portrait. Same SSR/jsdom-safe shape as `useIsMobile`
 * (`lib/use-is-mobile.ts`): defaults to false (table) until mounted.
 */
function useIsNarrow(): boolean {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = (): void => setIsNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return isNarrow;
}

export interface ObservedActivityPanelProps {
  state: UseAgentActivityState;
  agent: AgentDef;
}

/**
 * "2 hours ago" style — same math as `formatRelativeTime` in
 * `lib/format.ts`, but the verbose spelling matches the ADR §5 example
 * copy (`last seen 2 hours ago`). Local to the panel because no other
 * surface currently spells out the units.
 */
function relativeAge(iso: string | null, now: number = Date.now()): string {
  if (iso === null) return 'never';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const seconds = Math.max(0, Math.round((now - ms) / 1000));
  if (seconds < 60) {
    return seconds === 1 ? '1 second ago' : `${seconds} seconds ago`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  const days = Math.round(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

/** Map an audit `decision` string to the semantic-palette pill tone. */
function decisionTone(decision: string): 'lime' | 'amber' | 'coral' | 'sky' | 'mint' | 'neutral' {
  switch (decision) {
    case 'approved':
      return 'lime';
    case 'observed':
      return 'sky';
    case 'confirmed':
      return 'mint';
    case 'escalated':
    case 'escalated_approved':
      return 'amber';
    case 'rejected':
    case 'verification_rejected':
    case 'rejected_post_verify':
    case 'escalated_rejected':
      return 'coral';
    default:
      return 'neutral';
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

export function ObservedActivityPanel({
  state,
  agent,
}: ObservedActivityPanelProps): JSX.Element {
  const C = useC();
  const isNarrow = useIsNarrow();

  const cellStyle: CSSProperties = {
    fontFamily: monoFont,
    fontSize: 10,
    color: C.text,
    padding: '6px 8px',
    verticalAlign: 'top',
  };
  const headerCellStyle: CSSProperties = {
    fontFamily: monoFont,
    fontSize: 9,
    color: C.muted,
    letterSpacing: 1,
    textTransform: 'uppercase',
    padding: '6px 8px',
    textAlign: 'left',
    fontWeight: 500,
    borderBottom: `1px solid ${C.border}`,
  };

  return (
    <Card className="p-4 mt-5" aria-label="Observed agent activity">
      <div className="flex items-center gap-2 mb-3">
        {/* OBSERVED kicker — mirrors the LIVE tag styling in
            EndpointColumn.tsx (mono, uppercase, letter-spacing 1,
            lime accent). */}
        <span
          className="flex items-center gap-1"
          style={{
            fontFamily: monoFont,
            fontSize: 8,
            color: C.limeFg,
            letterSpacing: 1,
          }}
        >
          <Radio size={9} color={C.limeFg} /> OBSERVED
        </span>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 0.5,
          }}
        >
          · last 7 days
        </span>
      </div>
      {renderBody(state, agent, C, cellStyle, headerCellStyle, isNarrow)}
    </Card>
  );
}

function renderBody(
  state: UseAgentActivityState,
  agent: AgentDef,
  C: ReturnType<typeof useC>,
  cellStyle: CSSProperties,
  headerCellStyle: CSSProperties,
  isNarrow: boolean,
): JSX.Element {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          padding: '8px 0',
        }}
      >
        Loading observed activity…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          padding: '8px 0',
        }}
      >
        (couldn't load activity)
      </div>
    );
  }
  const { summary, recent_visits } = state.data;
  if (summary.visit_count === 0) {
    return (
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.5,
          padding: '4px 0',
        }}
      >
        No observed visits from {agent.name} yet.
      </div>
    );
  }

  // Summary line. Pluralize "time" honestly.
  const timesWord = summary.visit_count === 1 ? 'time' : 'times';
  const lastSeen = relativeAge(summary.last_seen);
  return (
    <>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 14,
          color: C.text,
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        <strong>{agent.name}</strong> visited {summary.visit_count.toLocaleString('en-US')} {timesWord} this
        week · last seen {lastSeen}
      </div>
      {isNarrow ? (
        // ADR-0163 §6 — below 640px the six fixed-width columns crush to
        // unreadable; re-lay each visit as a stacked card. Same text
        // nodes as the table cells (test-pinned content is layout-
        // agnostic); internal scroll cap preserved.
        <div style={{ maxHeight: 320, overflowY: 'auto' }} className="space-y-2">
          {recent_visits.map((v, i) => (
            <div
              key={`${v.evaluated_at}:${i}`}
              className="p-2"
              style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2, background: C.surface2 }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone={decisionTone(v.decision)}>{v.decision}</Pill>
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.text }}>{relativeAge(v.evaluated_at)}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span style={{ fontFamily: monoFont, fontSize: 10, color: v.skill_id === null ? C.dim : C.text }}>
                  {v.skill_id ?? '—'}
                </span>
              </div>
              <div
                className="mt-1"
                style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={v.user_agent ?? ''} /* title-ok: full value of the truncated user agent */
              >
                {v.user_agent === null ? '—' : truncate(v.user_agent, 64)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          // Up to 5 rows visible, overflow scrolls internally to the LIMIT 20
          // the wire returns. Row height ~30px, header ~26px → ~176px cap.
          style={{
            maxHeight: 176,
            overflowY: 'auto',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}
          >
            <thead>
              <tr style={{ background: C.surface2 }}>
  <th style={{ ...headerCellStyle, width: '20%' }}>Time</th>
              <th style={{ ...headerCellStyle, width: '16%' }}>Decision</th>
              <th style={{ ...headerCellStyle, width: '22%' }}>Skill</th>
              <th style={{ ...headerCellStyle, width: '42%' }}>User-Agent</th>
              </tr>
            </thead>
            <tbody>
              {recent_visits.map((v, i) => (
                <ObservedVisitRow key={`${v.evaluated_at}:${i}`} visit={v} cellStyle={cellStyle} C={C} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface ObservedVisitRowProps {
  visit: ReportingAgentActivityVisit;
  cellStyle: CSSProperties;
  C: ReturnType<typeof useC>;
}

function ObservedVisitRow({ visit, cellStyle, C }: ObservedVisitRowProps): JSX.Element {
  return (
    <tr style={{ borderTop: `1px solid ${C.borderSoft}` }}>
      <td style={cellStyle}>
        <div style={{ color: C.text }}>{relativeAge(visit.evaluated_at)}</div>
        <div style={{ color: C.muted, fontSize: 9 }}>{visit.evaluated_at}</div>
      </td>
      <td style={cellStyle}>
        <Pill tone={decisionTone(visit.decision)}>{visit.decision}</Pill>
      </td>
      <td style={{ ...cellStyle, color: visit.skill_id === null ? C.dim : C.text }}>
        {visit.skill_id ?? '—'}
      </td>
      <td
        style={{
          ...cellStyle,
          color: C.muted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={visit.user_agent ?? ''} /* title-ok: full value of the truncated user agent */
      >
        {visit.user_agent === null ? '—' : truncate(visit.user_agent, 48)}
      </td>
    </tr>
  );
}
